import React, { useState } from 'react';
import { Settings, X, Save, RefreshCw } from 'lucide-react';
import { useRAGStore } from '../store/ragStore';

import { getValidatedApiConfig } from '../utils/apiConfig';

// Get API base URL with validation
const apiConfig = getValidatedApiConfig();
const RAG_API_BASE: string = apiConfig.baseUrl;

const SettingsModal: React.FC = () => {
  const { baseUrl, setBaseUrl } = useRAGStore();
  const [isOpen, setIsOpen] = useState(false);
  const [tempBaseUrl, setTempBaseUrl] = useState(baseUrl);
  const [isSaving, setIsSaving] = useState(false);

  const handleOpen = () => {
    setTempBaseUrl(baseUrl);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTempBaseUrl(baseUrl);
  };

  const handleSave = async () => {
    if (!tempBaseUrl.trim()) {
      alert('Please enter a valid base URL');
      return;
    }

    setIsSaving(true);
    try {
      setBaseUrl(tempBaseUrl.trim());
              alert('Settings saved successfully');
      setIsOpen(false);
    } catch (error) {
              alert('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setTempBaseUrl(RAG_API_BASE);
  };

  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200"
        title="Settings"
      >
        <Settings className="h-5 w-5" />
      </button>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Settings</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-6">
            {/* API Configuration */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-3">API Configuration</h3>
              
              <div className="space-y-3">
                <div>
                  <label htmlFor="baseUrl" className="block text-sm font-medium text-gray-700 mb-2">
                    Base URL
                  </label>
                  <input
                    id="baseUrl"
                    type="url"
                    value={tempBaseUrl}
                    onChange={(e) => setTempBaseUrl(e.target.value)}
                    className="input-field"
                    placeholder="http://localhost:7773"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The base URL for the RAG API server
                  </p>
                </div>
              </div>
            </div>
            
            {/* Current Status */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Current Configuration</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Base URL:</span>
                  <span className="font-mono">{baseUrl}</span>
                </div>
              </div>
            </div>
            
            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">About Settings</h4>
              <p className="text-sm text-blue-800">
                These settings control how the application connects to your RAG API server. 
                Make sure the base URL points to a running RAG API instance.
              </p>
            </div>
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200">
            <button
              onClick={handleReset}
              className="btn-secondary flex items-center space-x-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Reset to Default</span>
            </button>
            
            <div className="flex space-x-3">
              <button
                onClick={handleClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              
              <button
                onClick={handleSave}
                disabled={isSaving || tempBaseUrl.trim() === baseUrl}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsModal;
